import { Track } from '@player/types';
import {Table, TableProps} from '@common/ui/tables/table';
import {ColumnConfig} from '@common/datatable/column-config';
import {Trans} from '@ui/i18n/trans';
import React, {useMemo, Fragment} from 'react';
import {AlbumLink} from '@app/web-player/albums/album-link';

import {FormattedDuration} from '@ui/i18n/formatted-duration';

import {TrackNameColumn} from '@app/web-player/tracks/track-table/track-name-column';
import {TrackTableMeta} from '@app/web-player/tracks/track-table/use-track-table-meta';
import {Skeleton} from '@ui/skeleton/skeleton';

import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {RowElementProps} from '@common/ui/tables/table-row';
import {TableTrackContextDialog} from '@app/web-player/tracks/context-dialog/table-track-context-dialog';

import { trackToMediaItem } from '@app/web-player/tracks/utils/track-to-media-item';
import { usePlayerActions } from '@common/player/hooks/use-player-actions';
import { TrackOptionsColumn } from '@app/web-player/tracks/track-table/track-options-column';
import { TableDataItem } from '@common/ui/tables/types/table-data-item';
import { useIsMobileDevice } from '@ui/utils/hooks/is-mobile-device';
import { Playlist } from '@app/web-player/playlists/playlist';


import { Link } from 'react-router'
import { IndexTrackColumn } from '@player/custom/IndexTrackColumn';
import clsx from 'clsx';

const columnConfig: ColumnConfig<Track>[] = [
    {
        key: 'index',
        header: () => <span></span>,
        align: 'center',
        width: 'flex-0 flex-shrink-0',
        body: (track, row) => <IndexTrackColumn track={track} row={row} />
    },
    {
        key: 'index_full',
        header: () => <span className=""></span>,
        align: 'center',
        width: 'flex-0 flex-shrink-0 min-w-140',
        body: (track, row) => <IndexTrackColumn track={track} row={row} />
    },
    {
        key: 'name',
        allowsSorting: true,
        width: 'flex-3 min-w-340',
        visibleInMode: 'all',
        header: () => (
            <>
                <Trans message="Title" /><br />
                <Trans message="Artist" />
            </>
        ),
        body: (track, row) => {
            if (row.isPlaceholder) {
                return (
                    <div
                        className={clsx('flex w-full max-w-4/5 items-center gap-12')}
                    >
                        <Skeleton
                            size="w-40 h-40 md:w-60 md:h-60 border"
                            variant="rect"
                        />
                        <div className="flex-auto">
                            <div>
                                <Skeleton />
                            </div>
                        </div>
                    </div>
                )
            }

            return <TrackNameColumn track={track} />
        },
    },
    {
        key: 'album_name',
        allowsSorting: true,
        width: 'flex-2',
        header: () => (
            <>
                <Trans message="Album" /><br />
                <Trans message="Label" />
            </>
        ),
        body: (track, row) => {
            if (row.isPlaceholder) {
                return <Skeleton className="max-w-4/5 leading-3" />;
            }
            return (
                <div className="flex flex-col items-start justify-center gap-2">
                    {track.album ? <AlbumLink className="block max-w-160" album={track.album} /> : null}
                    {track?.tags && track?.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {track.tags.map((tag, index) => (
                                <Fragment  key={tag.id}>
                                    {index > 0 && ', '}
                                    <Link
                                        to={`/tag/${tag.name}`}
                                        className="text-xs text-muted bg-muted/10 hover:underline rounded-full px-2 py-1"
                                    >
                                        {tag?.display_name || tag.name}
                                    </Link>
                                </Fragment>
                            ))}
                        </div>
                    )}
                </div>
            )
        },
    },
    {
        key: 'genres',
        allowsSorting: false,
        maxWidth: 'flex-0 flex-shrink-0 min-w-140',
        header: () => (
            <>
                <Trans message="Genres" /><br />
                <Trans message="BPM & Key" />
            </>
        ),
        body: (track, row) => {
            if (row.isPlaceholder) {
                return <Skeleton className="max-w-4/5 leading-3" />;
            }

            const genres = track?.genres?.map(genre => genre?.display_name || genre.name).join(' / ') || ''

            return (
                <div className="flex text-xs text-muted flex-col items-start justify-center gap-2">
                    <span title={genres} className="block w-140 truncate">{genres}</span>
                    <span className="block w-140 truncate">{track?.bpm || ''} BPM - {track?.key || ''}</span>
                </div>
            )
        },
    },
    // {
    //     key: 'release_date',
    //     //sortingKey: 'likes.created_at',
    //     allowsSorting: true,
    //     maxWidth: 'max-w-120 text-right',
    //     align: 'end',
    //     header: () => <Trans message="Released at" />,
    //     body: (track, row) => {
    //         if (row.isPlaceholder) {
    //             return <Skeleton className="max-w-4/5 leading-3" />;
    //         }
    //         return (
    //             <span className="text-xs text-muted">{track.release_date}</span>
    //         )
    //     },
    // },
    {
        key: 'options',
        align: 'end',
        width: 'flex-0 flex-shrink-0 md:min-w-136',
        header: () => <Trans message="Options" />,
        hideHeader: true,
        visibleInMode: 'all',
        body: (track, row) => {
            if (row.isPlaceholder) {
                return (
                    <div className="flex justify-end">
                        <Skeleton size="w-20 h-20" variant="rect" />
                    </div>
                );
            }
            return <TrackOptionsColumn track={track} isHovered={row.isHovered} />;
        },
    },
    {
        key: 'duration',
        allowsSorting: true,
        className: 'text-muted text-right',
        maxWidth: 'max-w-200 flex-shrink-0',
        align: 'end',
        header: () => (
            <>
                <Trans message="Time length" /><br />
                <Trans message="Released" />
            </>
        ),
        body: (track, row) => {
            if (row.isPlaceholder) {
                return <Skeleton className="leading-3" />;
            }
            return (
                <>
                    {track.duration ? <FormattedDuration ms={track.duration} /> : null}
                    <span className="text-xs text-muted block">{track.release_date}</span>
                </>
            )
        },
    }
];

export interface TrackTableProps {
    tracks: Track[] | TableDataItem[]; // might be passing in placeholder items for skeletons
    hideArtist?: boolean;
    hideAlbum?: boolean;
    hideTrackImage?: boolean;
    hidePopularity?: boolean;
    hideAddedAtColumn?: boolean;
    hideHeaderRow?: boolean;
    queueGroupId?: string | number;
    playlist?: Playlist;
    renderRowAs?: TableProps<Track>['renderRowAs'];
    sortDescriptor?: TableProps<Track>['sortDescriptor'];
    onSortChange?: TableProps<Track>['onSortChange'];
    enableSorting?: TableProps<Track>['enableSorting'];
    tableBody?: TableProps<Track>['tableBody'];
    hideGenres?: boolean;
    className?: string;
    hidePlaylistButton?: boolean;
    hideQueueButton?: boolean;
}
export function TrackTable(
    {
        tracks,
        hideArtist = false,
        hideAlbum = false,
        hideHeaderRow = false,
        hideTrackImage = false,
        hidePopularity = true,
        hideAddedAtColumn = true,
        hideGenres = false,
        hidePlaylistButton = false,
        hideQueueButton = false,
        queueGroupId,
        renderRowAs,
        playlist,
        ...tableProps
    }: TrackTableProps
) {
    const player = usePlayerActions();
    const isMobile = useIsMobileDevice();
    hideHeaderRow = hideHeaderRow || isMobile;

    const filteredColumns = useMemo(() => {
        return columnConfig.filter(col => {
            if (col.key === 'artist' && hideArtist) {
                return false;
            }
            if (col.key === 'album_name' && hideAlbum) {
                return false;
            }
            if (col.key === 'popularity' && hidePopularity) {
                return false;
            }
            if (col.key === 'added_at' && hideAddedAtColumn) {
                return false;
            }

            if (col.key === 'release_date' && hideAddedAtColumn) {
                return false;
            }

            if (col.key === 'genres' && hideGenres) {
                return false;
            }

            if (col.key === 'index' && !hideQueueButton && !hidePlaylistButton) {
                return false
            }

            if (col.key === 'index_full' && (hideQueueButton || hidePlaylistButton)) {
                return false
            }

            return true;
        });
    }, [hideArtist, hideAlbum, hidePopularity, hideAddedAtColumn, hideGenres]);

    const meta: TrackTableMeta = useMemo(() => {
        return {queueGroupId: queueGroupId, hideTrackImage, playlist, hidePlaylistButton, hideQueueButton};
    }, [queueGroupId, hideTrackImage, playlist, hidePlaylistButton, hideQueueButton]);

    return (
        <Table
            closeOnInteractOutside
            hideHeaderRow={hideHeaderRow}
            selectionStyle="highlight"
            className="relative"
            selectRowOnContextMenu
            renderRowAs={renderRowAs || TrackTableRowWithContextMenu}
            columns={filteredColumns}
            data={tracks as Track[]}
            meta={meta}
            hideBorder={isMobile}
            onAction={(track, index) => {
                const newQueue = tracks.map(d =>
                    trackToMediaItem(d as Track, queueGroupId),
                );
                player.overrideQueueAndPlay(newQueue, index);
            }}
            {...tableProps}
        />
    );
}

function TrackTableRowWithContextMenu(
    {
        item,
        children,
        ...domProps
    }: RowElementProps<Track>
) {
    const row = <div {...domProps}>{children}</div>;
    if (item.isPlaceholder) {
        return row;
    }
    return (
        <DialogTrigger
            type="popover"
            mobileType="tray"
            triggerOnContextMenu
            placement="bottom-start"
        >
            {row}
            <TableTrackContextDialog showAddToQueueButton={false} />
        </DialogTrigger>
    );
}
